import generated from './generated/help-content.generated.json';
import routeData from './help-routes.json';
import { HelpArticle, HelpCapabilityCheck, HelpRouteDefinition } from './types';

export const helpArticles = generated.articles as HelpArticle[];
export const helpRoutes = routeData as HelpRouteDefinition[];
export const unknownHelpRoute: HelpRouteDefinition = {
  id: 'general',
  path: '',
  label: 'Synergist Pro',
  capabilities: [],
};

const normalizePath = (pathname: string) => {
  const clean = (pathname || '/').split(/[?#]/, 1)[0].replace(/\/+$/, '');
  return clean || '/';
};

export const resolveHelpRoute = (pathname: string): HelpRouteDefinition => {
  const normalized = normalizePath(pathname);
  return helpRoutes
    .filter(route => normalized === route.path || (route.path !== '/' && normalized.startsWith(`${route.path}/`)))
    .sort((left, right) => right.path.length - left.path.length)[0] || unknownHelpRoute;
};

export const canReadHelpArticle = (article: HelpArticle, hasCapability: HelpCapabilityCheck) =>
  article.status === 'published' && article.capabilities.every(hasCapability);

export const getVisibleHelpArticles = (hasCapability: HelpCapabilityCheck) =>
  helpArticles.filter(article => canReadHelpArticle(article, hasCapability));

const normalizeTerms = (query: string) => query
  .toLowerCase()
  .trim()
  .split(/\s+/)
  .map(term => term.replace(/[^a-z0-9-]/g, ''))
  .filter(Boolean);

export const searchHelpArticles = (
  articles: HelpArticle[],
  query: string,
  routeId?: string,
) => {
  const terms = normalizeTerms(query);
  return articles
    .map(article => {
      const routeMatch = routeId && article.routes.includes(routeId) ? 1 : 0;
      const title = article.title.toLowerCase();
      const score = terms.reduce((total, term) => total
        + (title.includes(term) ? 8 : 0)
        + (article.keywords.some(keyword => keyword.toLowerCase().includes(term)) ? 4 : 0)
        + (article.searchText.includes(term) ? 1 : 0), routeMatch * 3);
      const allTermsMatch = terms.every(term => article.searchText.includes(term));
      return { article, score, allTermsMatch };
    })
    .filter(result => terms.length === 0 || result.allTermsMatch)
    .sort((left, right) => right.score - left.score || left.article.title.localeCompare(right.article.title))
    .map(result => result.article);
};

export const getContextHelpArticles = (
  articles: HelpArticle[],
  routeId: string,
) => articles
  .filter(article => article.routes.includes(routeId))
  .sort((left, right) => left.title.localeCompare(right.title));

export const getHelpArticle = (slug: string, hasCapability: HelpCapabilityCheck) =>
  helpArticles.find(article => article.slug === slug && canReadHelpArticle(article, hasCapability));

export const buildSupportContextUrl = (routeId: string) => {
  const known = helpRoutes.some(route => route.id === routeId) || routeId === unknownHelpRoute.id;
  const safeRoute = known ? routeId : unknownHelpRoute.id;
  return `https://app.synergistpro.io/help-context/${encodeURIComponent(safeRoute)}`;
};
